import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Fade,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useSearchParams } from 'react-router-dom';

import { useCheckPermissions } from '@/shared';
import { PermissionsEnum } from '@/auth/interfaces';
import { useVision360Cliente } from '@/shared/actions/erp/tickets-soporte-cliente';
import type {
  IClienteBusqueda,
  IVision360Contrato,
  IVision360Data,
} from '@/shared/interfaces/erp/tickets-soporte-clientes';

import Vision360Panel from '../../shared/components/Vision360Panel/Vision360Panel';
import FormCrearTicket from '../../shared/components/FormCrearTicket/FormCrearTicket';

export interface NuevoTicketPageInterface { }

const NuevoTicketPage: React.FC<NuevoTicketPageInterface> = () => {
  useCheckPermissions(PermissionsEnum.api_view_ticket_soporte);

  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedCliente, setSelectedCliente] = useState<IClienteBusqueda | null>(null);
  const [selectedContrato, setSelectedContrato] = useState<IVision360Contrato | null>(null);
  const [openCrearTicket, setOpenCrearTicket] = useState(false);

  // Pre-cargar cliente desde query params (viene de la barra de busqueda del navbar)
  const clienteIdParam = searchParams.get('clienteId');
  const contratoIdParam = searchParams.get('contratoId');
  const crearTicketParam = searchParams.get('crearTicket');

  // Guardar contratoId antes de limpiar params. Se reasigna en cada cambio
  // de clienteIdParam: si el usuario vino sin contratoId el ref queda en
  // null y el Vision360Panel auto-selecciona el primer contrato del cliente.
  const contratoIdRef = useRef<string | null>(contratoIdParam);

  useEffect(() => {
    // Procesar el query param si:
    //  - es la primera carga (no hay selectedCliente), o
    //  - el usuario navego a otro cliente desde la barra de busqueda y el
    //    id del query es distinto al cliente actual.
    if (!clienteIdParam) {
      return;
    }
    const yaCargado = selectedCliente?.id === clienteIdParam;
    if (yaCargado) {
      return;
    }
    contratoIdRef.current = contratoIdParam || null;
    setSelectedCliente({
      id: clienteIdParam,
      contrato_id: contratoIdParam || '',
      cedula: '',
      nombre_completo: '',
      tipo_cliente: '',
      estado: '',
      contrato_codigo: '',
      direccion: '',
      ip: '',
      plan: '',
      tiene_masivo_activo: false,
      masivo_codigo: '',
    } as IClienteBusqueda);
    // Resetear el contrato seleccionado: el anterior pertenece al cliente
    // viejo. El segundo useEffect lo volvera a setear cuando llegue vision360
    // del cliente nuevo (usando contratoIdRef.current).
    setSelectedContrato(null);
    // Cerrar el modal del form si quedo abierto del cliente anterior.
    setOpenCrearTicket(false);
    // Limpiar los query params para que no se re-procesen
    setSearchParams({}, { replace: true });
  }, [clienteIdParam, contratoIdParam]);

  // Fetch Vision 360 when a client is selected
  const { data: vision360Res, isLoading: isLoadingVision } = useVision360Cliente(
    selectedCliente?.id || '',
    selectedCliente?.contrato_id || undefined,
    !!selectedCliente?.id
  );

  // vision360Res?.data puede ser IVision360Data directo o un wrapper legacy;
  // (x as any).data cubre el segundo caso sin romper el type guard.
  const vision360Data: IVision360Data | null =
    ((vision360Res?.data as any)?.data as IVision360Data) || vision360Res?.data || null;

  // Auto-seleccionar contrato cuando vision360Data carga y hay contratoId desde URL
  useEffect(() => {
    if (vision360Data && contratoIdRef.current && !selectedContrato) {
      const contrato = vision360Data.contratos?.find(
        (c: IVision360Contrato) => String(c.id_contrato) === String(contratoIdRef.current)
      );
      if (contrato) {
        setSelectedContrato(contrato);
      }
    }
  }, [vision360Data]);

  // Auto-abrir formulario de crear ticket si viene con crearTicket=true
  useEffect(() => {
    if (crearTicketParam === 'true' && vision360Data && !openCrearTicket) {
      setOpenCrearTicket(true);
    }
  }, [crearTicketParam, vision360Data]);

  const handleSelectContrato = (contrato: IVision360Contrato) => {
    setSelectedContrato(contrato);
  };

  return (
    <Box sx={{ mt: 2, ml: { xs: 0, md: 2 }, mr: { xs: 0, md: 2 } }}>
      {/* Empty state - cuando no hay cliente seleccionado */}
      {!selectedCliente && (
        <Fade in>
          <Paper sx={{ p: 6, textAlign: 'center' }}>
            <SearchIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              Busque un cliente para comenzar
            </Typography>
            <Typography variant="body2" color="text.disabled" sx={{ mt: 1 }}>
              Ingrese cédula, nombre, código de contrato o dirección IP en el buscador
            </Typography>
          </Paper>
        </Fade>
      )}

      {/* Vision 360 + Create button */}
      {selectedCliente && (
        <Fade in>
          <Box>
            {/* Vision 360 Panel */}
            {vision360Data && (
              <Vision360Panel
                data={vision360Data}
                selectedContrato={selectedContrato}
                onSelectContrato={handleSelectContrato}
                onCrearTicket={() => setOpenCrearTicket(true)}
                isLoading={isLoadingVision}
              />
            )}

            {isLoadingVision && !vision360Data && (
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">Cargando información del cliente...</Typography>
              </Paper>
            )}

            {/* Form Dialog */}
            {vision360Data && (
              <FormCrearTicket
                open={openCrearTicket}
                onClose={() => setOpenCrearTicket(false)}
                cliente={vision360Data.cliente}
                contrato={(() => { console.log('selectedContrato:', selectedContrato); return selectedContrato; })()}
                hasMasivoActivo={(vision360Data.masivos_activos || []).length > 0}
                tickets={vision360Data.tickets || []}
              />
            )}
          </Box>
        </Fade>
      )}
    </Box>
  );
};

export default NuevoTicketPage;
